import { useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'redux/store';
import { setIsFullscreen } from 'common/presentation/redux/uiState';

export function useFullscreen() {
  const dispatch = useDispatch();
  const isFullscreen = useSelector((state) => state.ui.isFullscreen);

  useEffect(() => {
    dispatch(setIsFullscreen({ isFullscreen: !!document.fullscreenElement }));

    const handleFullscreenChange = () => {
      dispatch(setIsFullscreen({ isFullscreen: !!document.fullscreenElement }));
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, [dispatch]);

  const enterFullscreen = useCallback(async () => {
    if (!document.fullscreenElement && document.documentElement.requestFullscreen) {
      try {
        await document.documentElement.requestFullscreen();
        dispatch(setIsFullscreen({ isFullscreen: true }));
      } catch (error) {
        console.error('Error entering fullscreen:', error);
        throw error;
      }
    }
  }, [dispatch]);

  const exitFullscreen = useCallback(async () => {
    if (document.fullscreenElement && document.exitFullscreen) {
      try {
        await document.exitFullscreen();
        dispatch(setIsFullscreen({ isFullscreen: false }));
      } catch (error) {
        console.error('Error exiting fullscreen:', error);
        throw error;
      }
    }
  }, [dispatch]);

  const toggleFullscreen = useCallback(async () => {
    if (isFullscreen) {
      await exitFullscreen();
    } else {
      await enterFullscreen();
    }
  }, [isFullscreen, enterFullscreen, exitFullscreen]);

  return {
    isFullscreen,
    enterFullscreen,
    exitFullscreen,
    toggleFullscreen,
    isFullscreenSupported: !!document.documentElement.requestFullscreen,
  };
}
